// Matches the chatbot's web component tags - e.g. <chatbot-response-toolbar trace-id="1"> and its
// closing tag - which the API embeds in the markdown of a response.
const CHATBOT_WEB_COMPONENT_TAG = /<\/?chatbot-[a-z0-9-]+(?:\s[^>]*?)?\/?>/gi;

// Matches a response toolbar tag and captures the trace ID it carries - e.g. the "1" in
// <chatbot-response-toolbar trace-id="1">. Neither [^>]* nor the captured value can cross the
// tag's own ">", so the trace ID always belongs to that toolbar and not to some later element.
const RESPONSE_TOOLBAR_TRACE_ID = /<chatbot-response-toolbar[^>]*?\strace-id\s*=\s*(["'])(.*?)\1/gi;

// Whether a response is the one whose toolbar carries this trace ID. The attribute is compared
// as a whole value, rather than searching the response for the ID, so a trace ID that also
// appears in the chatbot's own prose or in another element's attribute can't match.
const hasResponseToolbar = (text, traceId) =>
  [...(text ?? '').matchAll(RESPONSE_TOOLBAR_TRACE_ID)].some(([, , id]) => id === traceId);

/**
 * Removes the chatbot's web components - the response toolbar, tool approval requests and the
 * function calls nested in them - from a response, leaving only the text the chatbot wrote.
 * @param {string} text - The raw markdown of a response.
 * @returns {string} The response text without web components.
 */
export const stripChatbotWebComponents = text =>
  (text ?? '')
    .replace(CHATBOT_WEB_COMPONENT_TAG, '')
    // Collapse the blank lines left behind by removed components.
    .replace(/\n{3,}/g, '\n\n')
    .trim();

/**
 * Finds the text of the response a toolbar belongs to, ready to be copied to the clipboard.
 * The toolbar is embedded in its own response's markdown as a web component tag carrying the
 * trace ID, so that ID identifies which response the toolbar was rendered in.
 * @param {object[]} messages - The chat messages, as returned by Deep Chat's getMessages().
 * @param {string} traceId - The trace ID of the response's toolbar.
 * @returns {string} The response text, or an empty string when the response can't be found.
 */
export const getResponseTextToCopy = (messages, traceId) => {
  if (!traceId) {
    return '';
  }

  const response = messages?.find(
    ({ role, text }) => role === 'ai' && hasResponseToolbar(text, traceId)
  );

  return stripChatbotWebComponents(response?.text);
};
